import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Button, CircularProgress, Autocomplete,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DrawerShell from './_drawer/DrawerShell';
import DrawerHeader from './_drawer/DrawerHeader';
import useResponsiveAnchor from './_drawer/useResponsiveAnchor';
import api from '../../utils/api';
import { INDIAN_CITIES, COURSE_OPTIONS } from '../../utils/indianCities';
import { RADIUS_LIBRARIAN as RADIUS } from '../../theme/tokens';

function calculateAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

const DEFAULT_FORM = {
  name: '',
  email: '',
  phone: '',
  dateOfBirth: null,
  city: null,
  interested_courses: [],
};

function StudentFormDrawer({ open, onClose, student, onSuccess, showSnackbar }) {
  const anchor = useResponsiveAnchor();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [errors, setErrors] = useState({});
  const isEdit = !!student;

  useEffect(() => {
    if (!open) return;
    if (student) {
      let estimatedDob = null;
      if (student.age) {
        const today = new Date();
        estimatedDob = new Date(today.getFullYear() - student.age, 0, 1);
      }
      let courses = [];
      if (student.interested_courses) {
        courses = Array.isArray(student.interested_courses)
          ? student.interested_courses
          : student.interested_courses.split(',').map((c) => c.trim()).filter(Boolean);
      }
      setForm({
        name: student.name || '',
        email: student.email || '',
        phone: student.phone || '',
        dateOfBirth: estimatedDob,
        city: student.city || null,
        interested_courses: courses,
      });
    } else {
      setForm(DEFAULT_FORM);
    }
    setErrors({});
  }, [student, open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: '' });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim()) errs.name = 'Name is required';
    if (!form.email.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email format';
    if (form.dateOfBirth) {
      const age = calculateAge(form.dateOfBirth);
      if (age < 5 || age > 120) errs.dateOfBirth = 'Please enter a valid date of birth';
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    try {
      setLoading(true);
      const payload = {
        name: form.name,
        email: form.email,
        phone: form.phone || null,
        age: form.dateOfBirth ? calculateAge(form.dateOfBirth) : null,
        city: form.city || null,
        interested_courses: form.interested_courses?.length > 0
          ? form.interested_courses.join(', ')
          : null,
      };

      let created = null;
      if (isEdit) {
        await api.put(`/librarian/students/${student.id}`, payload);
        showSnackbar('Student updated successfully', 'success');
      } else {
        const res = await api.post('/librarian/students', payload);
        created = res.data.data;
        showSnackbar('Student created successfully', 'success');
      }
      // Close first so the parent's onClose (which clears the selected student)
      // doesn't clobber the student that onSuccess opens the detail drawer for.
      onClose();
      onSuccess(created);
    } catch (err) {
      if (err.response?.data?.code === 'EMAIL_EXISTS') {
        setErrors({ email: 'A student with this email already exists' });
        showSnackbar('Email already exists', 'error');
      } else {
        showSnackbar(err.response?.data?.message || 'Failed to save student', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const footer = (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <Button onClick={onClose} disabled={loading} sx={{ borderRadius: RADIUS.md }}>
        Cancel
      </Button>
      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={loading}
        fullWidth
        sx={{ borderRadius: RADIUS.md, background: 'linear-gradient(135deg, #F26522 0%, #C94E10 100%)' }}
      >
        {loading ? <CircularProgress size={20} color="inherit" /> : isEdit ? 'Update' : 'Create'}
      </Button>
    </Box>
  );

  return (
    <DrawerShell
      open={open}
      onClose={onClose}
      anchor={anchor}
      ariaLabel={isEdit ? 'Edit student' : 'Add student'}
      footer={footer}
    >
      <DrawerHeader title={isEdit ? 'Edit Student' : 'Add New Student'} onClose={onClose} />
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Name *"
            name="name"
            value={form.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
            autoFocus
          />

          <TextField
            label="Email *"
            name="email"
            type="email"
            value={form.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
            disabled={isEdit}
          />

          <TextField
            label="Phone"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            fullWidth
          />

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Date of Birth"
              value={form.dateOfBirth}
              onChange={(v) => {
                setForm({ ...form, dateOfBirth: v });
                if (errors.dateOfBirth) setErrors({ ...errors, dateOfBirth: '' });
              }}
              maxDate={new Date()}
              minDate={new Date(1900, 0, 1)}
              slotProps={{
                textField: {
                  fullWidth: true,
                  error: !!errors.dateOfBirth,
                  helperText:
                    errors.dateOfBirth ||
                    (form.dateOfBirth ? `Age: ${calculateAge(form.dateOfBirth)} years` : ''),
                },
              }}
            />
          </LocalizationProvider>

          <Autocomplete
            options={INDIAN_CITIES}
            value={form.city}
            onChange={(_, v) => {
              setForm({ ...form, city: v });
              if (errors.city) setErrors({ ...errors, city: '' });
            }}
            renderInput={(params) => (
              <TextField {...params} label="City" error={!!errors.city} helperText={errors.city} />
            )}
            fullWidth
          />

          <Autocomplete
            multiple
            options={COURSE_OPTIONS}
            value={form.interested_courses}
            onChange={(_, v) => setForm({ ...form, interested_courses: v })}
            renderInput={(params) => (
              <TextField {...params} label="Interested Courses" placeholder="Select courses..." />
            )}
            fullWidth
            freeSolo
            filterSelectedOptions
          />
        </Box>
      </Box>
    </DrawerShell>
  );
}

export default StudentFormDrawer;
